import React, { Component } from 'react';
import Axios from 'axios';

import SimplePanel from '@/shared/components/SimplePanel';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import intlShape from '@/shared/utils/intlShape';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import api from '@/shared/api';
import config from '@/config';
import { showWhenPermission } from '@/shared/components/security';
import DataCacheService from '@/shared/services/DataCacheService';
import ScrollArrow from '@/shared/components/ScrollArrow';
import PerformanceBalanceFilters, { defaultFilters } from './PerformanceBalanceFilters';
import PerformanceBalanceVirtualTable from './PerformanceBalanceVirtualTable';
import messages from '../reportingMessages';
import { functionalities, privileges } from '@/shared/services/AuthService';

const SessionStorageKey = 'PerformanceBalanceReportParams';

const totalProperties = [
  'billableHours',
  'billableHoursAmount',
  'correctionHours',
  'correctionHoursAmount',
  'billableCostsAmount',
  'invoicedAmount',
  'balance',
];

const calculateTotals = rows =>
  rows.reduce((totals, row) => {
    totalProperties.forEach(prop => {
      // eslint-disable-next-line no-param-reassign
      totals[prop] = (totals[prop] || 0) + (row[prop] || 0);
    });
    return totals;
  }, {});

class PerformanceBalanceContainer extends Component {
  state = {
    reportFilters: {},
    rows: null,
    selectedRelationId: null,
  };

  constructor(props) {
    super(props);
    const sessionParams = JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};

    if (sessionParams.dateFrom) {
      sessionParams.dateFrom = moment(sessionParams.dateFrom);
    }
    if (sessionParams.dateUntil) {
      sessionParams.dateUntil = moment(sessionParams.dateUntil);
    }

    this.state = {
      ...this.state,
      reportFilters: {
        ...defaultFilters,
        ...sessionParams,
      },
    };
  }

  componentDidMount() {
    this.assignmentsEnabled = DataCacheService.assignmentsEnabled();
    this.retrieve();
  }

  componentWillUnmount() {
    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to leaving page.');
    }
  }

  getParams() {
    const { reportFilters } = this.state;
    const {
      relationId,
      dateFrom: from,
      dateUntil: until,
      relationTypes,
      invoicingType,
      ownCompanies,
      legalEntities,
      responsible,
      assistant,
      assignmentTemplateId,
      invoiceSchedule,
      withLabels,
      withoutLabels,
      teamCodes,
      associate,
      assistantBackup,
      billingUser,
      seniorUser,
      includeInactive,
      filterBehaviours,
      relationGroups,
      sectors,
      referenceYear,
    } = reportFilters;

    return {
      relationId,
      from,
      until,
      relationTypes,
      invoicingType,
      ownCompanies,
      legalEntities,
      responsible,
      assistant,
      assignmentTemplateId,
      invoiceSchedule,
      withLabels,
      withoutLabels,
      teamCodes,
      associate,
      assistantBackup,
      billingUser,
      seniorUser,
      includeInactive,
      filterBehaviours,
      relationGroups,
      sectors,
      referenceYear,
    };
  }

  retrieve() {
    const { reportFilters } = this.state;

    const params = this.getParams();

    const sessionParams = JSON.stringify(reportFilters);
    sessionStorage.setItem(SessionStorageKey, sessionParams);

    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to new request.');
    }
    this.reportToken = Axios.CancelToken.source();
    this.setState({ rows: null });
    return api.base
      .post(`${config.appUrls.reporting}/api/performancebalance`, params, {
        cancelToken: this.reportToken.token,
      })
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          rows: data,
        }));
        return data;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  }

  filtersChanged(filters) {
    this.setState(
      state => ({
        ...state,
        reportFilters: {
          ...state.reportFilters,
          ...filters,
        },
      }),
      () => this.retrieve(),
    );
  }

  handleRowClick = relationId => {
    this.setState({ selectedRelationId: relationId });
  };

  render() {
    const { intl } = this.props;
    const { reportFilters, rows, selectedRelationId } = this.state;
    const showAssignments = this.assignmentsEnabled;
    const totals = rows ? calculateTotals(rows) : {};

    return (
      <div className="relation-rentability">
        <SimplePanel
          article="27409410263964"
          title={intl.formatMessage(messages.PerformanceBalance)}
        >
          <div className="info-block container-fluid">
            <PerformanceBalanceFilters
              filters={reportFilters}
              onFilterChange={filters => this.filtersChanged(filters)}
            />
          </div>
        </SimplePanel>
        {rows ? (
          <>
            <PerformanceBalanceVirtualTable
              data={rows}
              showAssignments={showAssignments}
              totals={totals}
              reportFilters={reportFilters}
              selectedRelationId={selectedRelationId}
              onRowClick={this.handleRowClick}
            />
            <ScrollArrow />
          </>
        ) : (
          <LoadingAnimation />
        )}
      </div>
    );
  }
}

PerformanceBalanceContainer.propTypes = {
  intl: intlShape.isRequired,
};

export default showWhenPermission(
  functionalities.RentabilityReport,
  privileges.Read,
)(injectIntl(PerformanceBalanceContainer));
